import { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { Check, Upload, X } from "lucide-react";

import { PICTURE_PUZZLE } from "../../../../constants";
import { useApi } from "../../../../hooks/useApi";
import { JourneyContentRow } from "../../../../types/games";

/**
 * Props for the picture puzzle form component
 */
interface JourneyPicturePuzzleFormProps {
  contentRows: JourneyContentRow[];
  setContentRows: React.Dispatch<React.SetStateAction<JourneyContentRow[]>>;
  isLoading: boolean;
  teamId?: string | null; // Required for image upload API
  touchpointId: string; // ID of the touchpoint this form is associated with
}

/**
 * A purely presentational component for creating and editing picture puzzles in journeys.
 *
 * This component doesn't maintain its own state for rows - it only renders rows from
 * the parent component's state and calls parent methods to update that state.
 */
export const JourneyPicturePuzzleCreateForm = ({
  contentRows,
  setContentRows,
  isLoading,
  teamId,
  touchpointId,
}: JourneyPicturePuzzleFormProps) => {
  // Use API for file uploads
  const api = useApi();

  // Local UI state only (for tracking which rows are currently uploading)
  const [uploadingRows, setUploadingRows] = useState<{
    [key: number]: boolean;
  }>({});

  // Local state to track input values without losing focus on keystrokes
  const [localInputValues, setLocalInputValues] = useState<
    Record<number, Record<string, string>>
  >({});

  // Simplified version for checking completed rows in parent state
  const isRowComplete = (row: JourneyContentRow): boolean => {
    return !!(row.image_id && row.answer);
  };

  // Get all rows for this touchpoint and game type
  const allTouchpointRows = contentRows.filter(
    (row) => row.touchpoint_id === touchpointId,
  );

  // Get only the incomplete rows for editing
  const relevantRows = allTouchpointRows.filter((row) => !isRowComplete(row));

  // Filter rows for preview - only show rows with complete data
  const previewRows = allTouchpointRows.filter((row) => isRowComplete(row));

  // Add a new empty row if there are no relevant rows
  useEffect(() => {
    if (relevantRows.length === 0) {
      addEmptyRow();
    }
  }, [relevantRows.length]);

  // Add a new empty row to parent state
  const addEmptyRow = () => {
    const newRow = {
      id: Date.now(),
      touchpoint_id: touchpointId,
      game_type: PICTURE_PUZZLE,
    };

    setContentRows((prev) => [...prev, newRow]);
  };

  // Handle input change locally to prevent losing focus
  const handleInputChange = (id: number, field: string, value: string) => {
    setLocalInputValues((prev) => ({
      ...prev,
      [id]: {
        ...(prev[id] || {}),
        [field]: value,
      },
    }));
  };

  // Get value from local state first, fallback to parent state
  const getInputValue = (row: JourneyContentRow, field: string): string => {
    if (
      localInputValues[row.id] &&
      localInputValues[row.id][field] !== undefined
    ) {
      return localInputValues[row.id][field];
    }
    return row[field] || "";
  };

  // Handle local updates without modifying parent state
  const handleLocalUpdate = (id: number, field: string, value: any) => {
    // Just update the local state, parent state will be updated when saved
    handleInputChange(id, field, value);
  };

  // Remove a row from parent state
  const removeRow = (idToRemove: number) => {
    setContentRows((prevRows) => {
      // Filter out the row to remove
      const filteredRows = prevRows.filter((row) => row.id !== idToRemove);

      // Get the remaining rows for this touchpoint/game
      const remainingForTouchpoint = filteredRows.filter(
        (row) => row.touchpoint_id === touchpointId,
      );

      // If we removed the last row for this touchpoint, add a new empty one
      if (remainingForTouchpoint.length === 0) {
        return [
          ...filteredRows,
          {
            id: Date.now(),
            touchpoint_id: touchpointId,
            game_type: PICTURE_PUZZLE,
          },
        ];
      }

      return filteredRows;
    });
  };

  // Handle file upload for a specific row
  const handleFileUpload = async (
    event: React.ChangeEvent<HTMLInputElement>,
    rowId: number,
  ) => {
    const file = event.target.files?.[0];
    if (!file || !teamId) return;

    // Validate file type
    if (!file.type.startsWith("image/")) {
      toast.error("Please select an image file");
      return;
    }

    // Validate file size (5MB)
    const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB in bytes
    if (file.size > MAX_FILE_SIZE) {
      toast.error("Image size must be less than 5MB");
      return;
    }

    // Start uploading - update UI state only
    setUploadingRows((prev) => ({ ...prev, [rowId]: true }));

    try {
      // Create form data for the file
      const formData = new FormData();
      formData.append("file", file);

      const response = await api.post(
        `/v2/games/library/upload-image?team=${teamId}`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        },
      );

      const { image_id, url } = response.data;

      // Process the response
      // Update the row in parent state
      setContentRows((prevRows) => {
        return prevRows.map((row) => {
          if (row.id === rowId) {
            return {
              ...row,
              image_url: url,
              image_id: image_id,
              image_filename: file.name,
            };
          }
          return row;
        });
      });
    } catch (error) {
      console.error("Error uploading file:", error);
      toast.error("Failed to upload image. Please try again.");
    } finally {
      // Reset uploading state
      setUploadingRows((prev) => {
        const updated = { ...prev };
        delete updated[rowId];
        return updated;
      });
    }
  };

  // Check if a row has all required fields filled for the save button
  const isRowValid = (id: number): boolean => {
    const row = contentRows.find((r) => r.id === id);
    if (!row) return false;

    // Get values from local state for answer, and image from parent state
    const answer = getInputValue(row, "answer");
    const image = row.image_id;

    // At minimum we need an image and an answer
    return !!(image && answer);
  };

  // Save all valid rows from local state to parent state, replacing existing ones for this TP/Game
  const saveRowsToPreview = (e: React.FormEvent) => {
    e.preventDefault();

    // 1. Get newly valid rows from the ones being edited
    const newlyValidRows = relevantRows
      .map((row) => {
        if (isRowValid(row.id)) {
          // Construct the full row data from local state
          return {
            ...row, // Keep existing fields like id, touchpoint_id, game_type
            type: "picture_puzzle", // Ensure API compatibility type
            answer: getInputValue(row, "answer"),
            clue: getInputValue(row, "clue"),
          } as JourneyContentRow; // Cast to ContentRow type
        }
        return null; // Return null for invalid rows
      })
      .filter((row): row is JourneyContentRow => row !== null); // Filter out the nulls and assert type

    // Get already completed rows
    const completedRows = allTouchpointRows.filter((row) => isRowComplete(row));

    // Combine completed and newly valid rows
    const allValidRows = [...completedRows, ...newlyValidRows];

    if (newlyValidRows.length === 0) {
      toast.error("Please complete at least one picture puzzle before saving.");
      return;
    }

    // 2. Update parent state: save valid rows and add a new empty one
    setContentRows((prevRows) => {
      // Filter out all existing rows for this specific touchpoint and game
      const otherRows = prevRows.filter(
        (row) => !(row.touchpoint_id === touchpointId),
      );

      // Add all valid rows (both completed and newly valid) and a new empty row
      return [
        ...otherRows,
        ...allValidRows,
        {
          id: Date.now(),
          touchpoint_id: touchpointId,
          game_type: PICTURE_PUZZLE,
        },
      ];
    });

    // 3. Clear local state for the saved rows
    const savedRowIds = new Set(newlyValidRows.map((row) => row.id));
    setLocalInputValues((prev) => {
      const newState = { ...prev };
      savedRowIds.forEach((id) => {
        delete newState[id];
      });
      return newState;
    });

    toast.success(
      `${newlyValidRows.length} picture puzzle${
        newlyValidRows.length !== 1 ? "s" : ""
      } saved successfully!`,
    );
  };

  // If no rows exist for this touchpoint, add an empty row
  if (relevantRows.length === 0 && !isLoading) {
    // Use setTimeout to avoid React warnings about state updates during render
    setTimeout(() => addEmptyRow(), 0);
  }

  return (
    <div className="space-y-4 relative">
      {/* Saved Puzzles Display - show at top like other components */}
      {relevantRows.filter((row) => isRowComplete(row)).length > 0 && (
        <div className="mb-6 p-4 bg-gray-50 rounded-md border border-gray-200">
          <div className="flex justify-between items-center mb-2">
            <h4 className="text-md font-medium text-gray-900">Saved Puzzles</h4>
            <span className="text-xs text-gray-500">
              {relevantRows.filter((row) => isRowComplete(row)).length} total
            </span>
          </div>
          <div className="space-y-2 max-h-32 overflow-y-auto">
            {relevantRows
              .filter((row) => isRowComplete(row))
              .map((row) => (
                <div
                  key={`preview-${row.id}`}
                  className="flex items-center justify-between bg-white p-2 rounded-md text-sm border border-gray-100"
                >
                  <div className="flex items-center flex-1 truncate">
                    <div className="w-8 h-8 mr-3 bg-gray-200 rounded overflow-hidden">
                      {row.image_url && (
                        <img
                          src={row.image_url}
                          alt="Preview"
                          className="w-full h-full object-cover"
                        />
                      )}
                    </div>
                    <span className="font-medium">{row.answer}</span>
                    {(row.answer || row.clue) && (
                      <>
                        <span className="mx-2 text-brand font-medium">→</span>
                        <span className="text-gray-600">
                          {row.answer || row.clue}
                        </span>
                      </>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => removeRow(row.id)}
                    className="text-red-500 hover:text-red-700 p-1"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))}
          </div>
        </div>
      )}

      {/* Add Row button */}
      <div className="flex justify-end items-center mt-4 gap-x-4">
        <button
          onClick={saveRowsToPreview}
          disabled={isLoading}
          className="px-3 py-1 text-sm bg-brand bg-opacity-10 text-brand rounded-md hover:bg-opacity-20 transition-colors duration-150 border border-brand/20"
        >
          <Check className="w-4 h-4 inline-block pr-2" />
          {isLoading ? "Saving..." : "Save"}
        </button>

        <button
          type="button"
          onClick={addEmptyRow}
          className="px-3 py-1 text-sm bg-brand bg-opacity-10 text-brand rounded-md hover:bg-opacity-20 transition-colors duration-150 border border-brand/20"
        >
          + Add Row
        </button>
      </div>

      {/* Preview of saved items - only show when there are valid saved rows */}
      {previewRows.length > 0 && (
        <div className="bg-gray-50 p-3 rounded-md border border-gray-200 mb-4">
          <div className="flex justify-between items-center mb-2">
            <h4 className="text-sm font-medium text-gray-900">
              Saved Picture Puzzles
            </h4>
            <span className="text-xs text-gray-500">
              {previewRows.length} total
            </span>
          </div>
          <div className="space-y-2 max-h-32 overflow-y-auto">
            {previewRows.map((row) => (
              <div
                key={row.id}
                className="flex items-center justify-between bg-white p-2 rounded-md text-sm border border-gray-100"
              >
                <div className="flex-1 truncate">
                  <span className="font-medium">{row.answer}</span>
                  {row.clue && (
                    <>
                      <span className="mx-2 text-brand font-medium">→</span>
                      <span className="font-medium">{row.clue}</span>
                    </>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => removeRow(row.id)}
                  className="text-red-500 hover:text-red-700 p-1"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Form with multiple editable rows */}
      <form onSubmit={saveRowsToPreview} className="pt-4">
        {/* Display rows from parent state */}
        {relevantRows.map((row) => (
          <div
            key={row.id}
            className="flex flex-col p-3 rounded-md bg-white shadow-sm border border-gray-200"
          >
            {/* Image upload */}
            <div className="grid grid-cols-3 gap-4 items-center">
              <label
                htmlFor={`file-upload-${row.id}`}
                className="mt-5 h-10 border border-dashed border-gray-300 rounded-md flex items-center justify-center bg-gray-50 relative cursor-pointer hover:bg-gray-100 transition-colors duration-150"
              >
                {uploadingRows[row.id] ? (
                  <div className="animate-spin h-6 w-6 border-2 border-blue-500 border-t-transparent rounded-full"></div>
                ) : row.image_url ? (
                  <div className="flex items-center justify-center text-sm text-gray-600 overflow-hidden whitespace-nowrap">
                    {row.image_filename || "Image"}{" "}
                    <span className="text-xs ml-1">
                      <Upload size={12} />
                    </span>
                  </div>
                ) : (
                  <div className="text-center text-gray-400 text-sm">
                    Add image
                  </div>
                )}
                <input
                  id={`file-upload-${row.id}`}
                  type="file"
                  accept="image/*"
                  className="sr-only"
                  onChange={(e) => handleFileUpload(e, row.id)}
                  disabled={isLoading || uploadingRows[row.id]}
                />
              </label>

              {/* Answer input */}
              <div>
                <label className="block text-xs text-gray-600 mb-1">
                  Answer
                </label>
                <input
                  type="text"
                  value={getInputValue(row, "answer")}
                  onChange={(e) =>
                    handleLocalUpdate(row.id, "answer", e.target.value)
                  }
                  placeholder="Enter the answer"
                  className="w-full p-2 border border-gray-300 rounded-md text-sm focus:ring-brand focus:border-brand"
                  disabled={isLoading}
                />
              </div>

              {/* Clue input */}
              <div>
                <label className="block text-xs text-gray-600 mb-1">Clue</label>
                <input
                  type="text"
                  value={getInputValue(row, "clue")}
                  onChange={(e) =>
                    handleLocalUpdate(row.id, "clue", e.target.value)
                  }
                  placeholder="Enter a clue"
                  className="w-full p-2 border border-gray-300 rounded-md text-sm focus:ring-brand focus:border-brand"
                  disabled={isLoading}
                />
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end items-center mt-3 pt-3 border-t border-gray-100">
              <div>
                <button
                  type="button"
                  onClick={() => removeRow(row.id)}
                  className="p-1 text-red-600 hover:text-red-800 disabled:text-gray-400"
                  disabled={isLoading || relevantRows.length === 1} // Prevent removing the last row
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        ))}
      </form>
      <div>
        <span className="text-xs font-medium text-gray-700">
          {previewRows.length} puzzles ready
        </span>
      </div>
    </div>
  );
};
